<template>
    <Tabs />

    <DCard padding>
        <DRow
            align-horizontal="start"
            align-vertical="end"
            class="mb-3"
        >
            <DCol lg="2">
                <DInput
                    id="filters-customer-id"
                    v-model="filterData.customer_id"
                    :label="__('tools.bankruptcy_monitor.index.filters.customer_id')"
                    type="number"
                />
            </DCol>
            <DCol lg="3">
                <DInput
                    id="filters-name"
                    v-model="filterData.name"
                    :label="__('tools.bankruptcy_monitor.index.filters.name')"
                />
            </DCol>
            <DCol lg="2">
                <label for="action-dropdown">
                    {{ __('tools.bankruptcy_monitor.index.filters.sanity') }}
                </label>
                <div class="py-1" />
                <DMultiSelect
                    id="action-dropdown"
                    :options="sanityOptions"
                    selection-type="text"
                    @update="updateSelectedSanityOptions"
                />
            </DCol>
            <DCol lg="1">
                <label for="filter-open-contracts">
                    {{ __('tools.bankruptcy_monitor.index.filters.open_contracts') }}
                </label>
                <div class="py-1" />
                <div class="form-control border-0 pl-0">
                    <DSwitch
                        id="filter-open-contracts"
                        v-model="filterData.open_contracts"
                        color="success"
                        variant="3d"
                    />
                </div>
            </DCol>
            <DCol lg="1">
                <label for="filter-open-invoices">
                    {{ __('tools.bankruptcy_monitor.index.filters.open_invoices') }}
                </label>
                <div class="py-1" />
                <div class="form-control border-0 pl-0">
                    <DSwitch
                        id="filter-open-invoices"
                        v-model="filterData.open_invoices"
                        color="success"
                        variant="3d"
                    />
                </div>
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="changePage"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow class="mt-3">
            <DCol>
                <DTable
                    id="tableBankruptcyMonitor"
                    :items="items.data"
                    :fields="fields"
                    sorter
                >
                    <template #checkbox="{item}">
                        <td>
                            <input
                                class="mx-1"
                                type="checkbox"
                                :value="item.id"
                                :disabled="item.invoices_count < 1"
                                @change="toggleSelectionUser(item.id)"
                            >
                        </td>
                    </template>
                    <template #id="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.id)">
                                {{ item.id }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.id }}
                        </td>
                    </template>
                    <template #sanity="{item}">
                        <td>
                            {{ __('customers.common.sanity.' + item.sanity) }}
                        </td>
                    </template>
                </DTable>
                <DFabs>
                    <DFab
                        id="btn-add"
                        :disabled="isCreateFilesDisabled"
                        icon="file-export"
                        :label="__('tools.bankruptcy_monitor.index.btn_create_files')"
                        @click="createFiles"
                    />
                </DFabs>
                <DPagination
                    v-if="items.last_page > 1"
                    :active-page="items.current_page"
                    :pages="items.last_page"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
    </DCard>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { ref, computed, onMounted } from 'vue'
import Tabs from '~@/Components/BillingAndAccounting/BankruptcyMonitor/Tabs.vue'

const fields = [
    { key: 'checkbox', label: '', sorter: false, _style: { width: '10px' } },
    { key: 'id', label: __('tools.bankruptcy_monitor.index.table.customer_id'), sorter: true },
    { key: 'sanity', label: __('tools.bankruptcy_monitor.index.table.sanity'), sorter: false },
    { key: 'company', label: __('tools.bankruptcy_monitor.index.table.company'), sorter: false },
    { key: 'firstname', label: __('tools.bankruptcy_monitor.index.table.firstname'), sorter: false },
    { key: 'lastname', label: __('tools.bankruptcy_monitor.index.table.lastname'), sorter: false },
    { key: 'brand_name', label: __('tools.bankruptcy_monitor.index.table.brand_name'), sorter: false },
    { key: 'contracts_count', label: __('tools.bankruptcy_monitor.index.table.contracts_count'), sorter: true },
    { key: 'invoices_count', label: __('tools.bankruptcy_monitor.index.table.invoices_count'), sorter: true },
    {
        key: 'invoices_sum_total_amount',
        label: __('tools.bankruptcy_monitor.index.table.invoices_sum_total_amount'),
        sorter: false
    }
]

const props = defineProps({
    items: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    sanityOptions: {
        type: Array,
        default () {
            return []
        }
    }
})

const filterData = ref({
    customer_id: props.filters.customer_id ?? null,
    name: props.filters.name ?? null,
    sanity: props.filters.sanity ?? null,
    open_contracts: props.filters.open_contracts ?? null,
    open_invoices: props.filters.open_invoices ?? null
})

const selectedUserIds = ref([])
const isCreateFilesDisabled = ref(true)

const userPermissions = computed(() => usePage().props.auth.permissions)
const customerProfileRoute = (id) => route('customers.customers.show', id)

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }
    router.get(route('tools.bankruptcy-monitor.index', {
        save_filters: true
    }), query)
}

const updateSelectedSanityOptions = (values) => {
    filterData.value.sanity = values
}

const resetFilters = () => {
    router.get(route('tools.bankruptcy-monitor.index', {
        save_filters: true,
        default_filters: true
    }))
}

const selectAllItems = (event) => {
    const checkboxes = document.querySelectorAll('#tableBankruptcyMonitor tbody input[type="checkbox"]')
    const selectedIds = []
    checkboxes.forEach((checkbox) => {
        checkbox.checked = event.target.checked
        if (event.target.checked) {
            selectedIds.push(Number(checkbox.value))
        }
    })
    selectedUserIds.value = selectedIds
    isCreateFilesDisabled.value = selectedUserIds.value.length === 0
}

const unselectAllItems = () => {
    const checkboxes = document.querySelectorAll('#tableBankruptcyMonitor tbody input[type="checkbox"]')
    checkboxes.forEach((checkbox) => {
        checkbox.checked = false
    })
    selectedUserIds.value = []
    isCreateFilesDisabled.value = true
}

onMounted(() => {
    const firstTh = document.querySelector('#tableBankruptcyMonitor > div > table > thead > tr > th')
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.classList.add('mx-1')
    checkbox.addEventListener('change', (event) => {
        selectAllItems(event)
    })
    firstTh.prepend(checkbox)
})

const toggleSelectionUser = (userId) => {
    const index = selectedUserIds.value.indexOf(userId)
    if (index === -1) {
        selectedUserIds.value.push(userId)
    } else {
        selectedUserIds.value.splice(index, 1)
    }

    isCreateFilesDisabled.value = selectedUserIds.value.length === 0
}

const createFiles = () => {
    router.post(route('tools.bankruptcy-monitor.create-files'), { ids: selectedUserIds.value })

    const checkboxes = document.querySelectorAll('#tableBankruptcyMonitor thead input[type="checkbox"]')
    checkboxes.forEach((checkbox) => {
        checkbox.checked = false
    })

    unselectAllItems()
}

</script>
